import React, { useMemo, useState } from "react";
import loader from "../../../../../../components/recharts/loader.gif";
import RowDetailPopup from "../../Popups/RowDetailPopup";

import { STATE_NAMES } from "./Constants";

interface TableBuySellVolumeProps {
    data?: any; // TopBuyerSellerResponse
    loading?: boolean;
    viewType?: "buyers" | "sellers";
    selectedExchanges?: string[];
    selectedMarkets?: string[];
    selectedEntities?: { value: string; label: string }[];
}

const TableBuySellVolume: React.FC<TableBuySellVolumeProps> = ({
    data,
    loading = false,
    viewType = "buyers",
    selectedExchanges,
    selectedMarkets,
    selectedEntities = [],
}) => {
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any | null>(null);

    const handleRowClick = (row: any) => {
        if (STATE_NAMES.includes(row.name)) {
            setSelectedRow(row);
            setIsPopupOpen(true);
        }
    };

    const tableRows = useMemo(() => {
        if (!data) return [];
        const source = viewType === "buyers" ? (data.top_buyers || []) : (data.top_sellers || []);
        const filtered =
            selectedEntities.length > 0
                ? source.filter((item: any) =>
                    selectedEntities.some((e) => e.value === item.name),
                  )
                : source;
        return [...filtered].sort((a: any, b: any) => b.value - a.value).slice(0, 10);
    }, [data, viewType, selectedEntities]);

    if (loading) {
        return (
            <div className="w-full bg-white flex items-center justify-center min-h-[300px]">
                <img src={loader} width={100} alt="loader" className="block" />
            </div>
        );
    }

    return (
        <>
            <div className="w-full h-full overflow-auto">
                <table className="w-full">
                    <thead className="sticky top-0 z-[1]">
                        <tr>
                            <th className="py-1.55 px-1 text-left text-[11px] font-medium text-[#8C8A96] whitespace-nowrap !bg-[#EEF4FE]">
                                Rank
                            </th>
                            <th className="py-1.55 px-1 text-left text-[11px] font-medium text-[#8C8A96] whitespace-nowrap !bg-[#EEF4FE]">
                                {viewType === "buyers" ? "Buyer" : "Seller"}
                            </th>
                            <th className="py-1.55 px-1 text-left text-[11px] font-medium text-[#8C8A96] whitespace-nowrap !bg-[#EEF4FE]">
                                Value (MW)
                            </th>
                            <th className="py-1.55 px-1 text-left text-[11px] font-medium text-[#8C8A96] whitespace-nowrap !bg-[#EEF4FE]">
                                Region
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white">
                        {tableRows.length > 0 ? (
                            tableRows.map((row: any, index: number) => {
                                const isClickable = STATE_NAMES.includes(row.name);
                                return (
                                    <tr
                                        key={index}
                                        className={`border-b border-[#F0F4F8] hover:bg-[#F8FAFC] transition-colors ${isClickable ? "cursor-pointer" : "cursor-default"}`}
                                        onClick={() => handleRowClick(row)}
                                    >
                                        <td className="py-1.55 px-1 text-left whitespace-nowrap text-[11px] text-[#525062]">
                                            {row.rank}
                                        </td>
                                        <td className="py-1.55 px-1 text-left whitespace-nowrap text-[11px] font-medium text-[#34656D] max-w-[80px] truncate" title={row.name}>
                                            {row.name}
                                        </td>
                                        <td className="py-1.55 px-1 text-left whitespace-nowrap text-[11px] text-[#525062]">
                                            {row.value}
                                        </td>
                                        <td className="py-1.55 px-1 text-left whitespace-nowrap text-[11px] text-[#34656D]">
                                            {row.region}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={4} className="py-8 text-center text-gray-500">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Row Detail Popup - might need update if props structure changes heavily */}
            <RowDetailPopup
                isOpen={isPopupOpen}
                onClose={() => setIsPopupOpen(false)}
                rowData={selectedRow}
                selectedExchanges={selectedExchanges}
                selectedMarkets={selectedMarkets}
            />
        </>
    );
};

export default TableBuySellVolume;
